import {
  CustomerMergingStatus,
  hasRequiredPermission,
  PERMISSIONS,
  QuoteStatus,
  RuleStatusAsPerApiResponse
} from '@common/utils'
import {
  type ColumnCellTemplateData,
  MolsonBox,
  MolsonIcon,
  MolsonTypography
} from '@components/reusable'
import { molsonTheme } from '@theme'
import type { TFunction } from 'i18next'
import { OpenItemInfo } from '../open-item-info/OpenItemInfo'
import { WarningChip } from '../warning-chip/WarningChip'

type RootType =
  | 'generalQuotes'
  | 'customerQuotes'
  | 'customerSpecs'
  | 'rulesGrid'
  | 'approvalRulesGrid'
  | 'customersGrid'
  | 'accessRolesGrid'
  | 'usersGrid'
  | 'quoteActionsDialog'

type RenderItemItemsProps = {
  params: ColumnCellTemplateData
  handleOnClick: (() => void) | (() => Promise<void>)
  theme?: typeof molsonTheme
  root?: RootType
  t?: TFunction
}

export const renderItemNameColumn = ({
  params,
  handleOnClick,
  theme,
  root = 'generalQuotes',
  t
}: RenderItemItemsProps) => {
  const hasAccessToCustomerMerging = hasRequiredPermission(
    PERMISSIONS.CUSTOMERS_MERGING
  )

  const isNeedsRevisingStatus =
    (params.data.status === QuoteStatus.NEEDS_REVISING &&
      root === 'generalQuotes') ||
    (params.data.status === RuleStatusAsPerApiResponse.REVIEW_NEEDED &&
      root === 'rulesGrid')
  const isPriceChangedStatus =
    params.data.status === QuoteStatus.PRICE_CHANGED && root === 'generalQuotes'

  const hasDuplicates =
    hasAccessToCustomerMerging &&
    params.data.hasDuplicates &&
    root === 'customersGrid'

  const customerMergingInProgress =
    params.data.companyType === CustomerMergingStatus.MergeInProgress

  const handleTextKey = () => {
    if (
      root === 'generalQuotes' ||
      root === 'customerQuotes' ||
      root === 'quoteActionsDialog'
    ) {
      return 'quotationName'
    }
    if (
      root === 'customerSpecs' ||
      root === 'rulesGrid' ||
      root === 'approvalRulesGrid'
    ) {
      return 'name'
    }

    if (root === 'accessRolesGrid') {
      return 'displayName'
    }

    if (root === 'customersGrid') {
      return 'companyName'
    }
    if (root === 'usersGrid') {
      return 'userName'
    }
  }

  return (
    <MolsonBox display="flex" alignItems="center">
      <OpenItemInfo
        customSx={{
          alignItems: 'center',
          display: 'flex',
          gap: 2,
          justifyContent: 'space-between'
        }}
        onClick={handleOnClick}
        text={params.data[handleTextKey() ?? '']}
      >
        {isNeedsRevisingStatus && (
          <MolsonIcon variant="ERROR_OUTLINE" iconColor="error" />
        )}

        {isPriceChangedStatus && (
          <MolsonIcon variant="WARNING" iconColor="warning" />
        )}

        {hasDuplicates && !customerMergingInProgress && (
          <WarningChip text={t?.('common.potentialDuplicates') ?? ''} />
        )}

        {customerMergingInProgress && (
          <WarningChip text={t?.('grid.mergeInProgress') ?? ''} />
        )}
      </OpenItemInfo>
      {root === 'customerSpecs' && (
        <MolsonTypography
          bgcolor={theme?.palette.secondary[200]}
          borderRadius={4.5}
          padding={1.5}
          variant="subtitle2"
        >
          {params.data.templates?.length} {t?.('common.items')}
        </MolsonTypography>
      )}
    </MolsonBox>
  )
}
